import { GetStaticProps, InferGetStaticPropsType, NextPage } from 'next';

import {
  EcusCardsSectionDocument,
  EcusCardsSectionQuery,
  EcusCardsSectionQueryVariables,
  EcusHeroDocument,
  EcusHeroQuery,
  EcusHeroQueryVariables,
  EcusPartnersBlogSectionDocument,
  EcusPartnersBlogSectionQuery,
  EcusPartnersBlogSectionQueryVariables,
  EcusProductImageBlockDocument,
  EcusProductImageBlockQuery,
  EcusProductImageBlockQueryVariables,
  EcusSeoDocument,
  EcusSliderImageSectionDocument,
  EcusSliderImageSectionQuery,
  EcusSliderImageSectionQueryVariables,
  EcusTwoImagesGridDocument,
  EcusTwoImagesGridQuery,
  EcusTwoImagesGridQueryVariables,
  FooterDocument,
  FooterQuery,
  FooterQueryVariables,
  FormDocument,
  FormQuery,
  FormQueryVariables,
  NavigationDocument,
  NavigationQuery,
  NavigationQueryVariables,
  ProductImageBlockFragment,
} from '@/api';
import { addApolloState, initializeApollo } from '@/api/apolloClient';
import { Layout } from '@/components/layout/Layout';
import { Partners } from '@/components/partners/Partners';
import { Seo } from '@/components/Seo';
import EnhancedProductImageBlock from '@/components/shared/EnhancedProductImageBlock';
import HeroVideoSection from '@/components/shared/HeroVideoSection';
import IconCards from '@/components/shared/IconCards';
import SliderImage from '@/components/shared/SliderImage';
import TwoImagesGrid from '@/components/shared/TwoImagesGrid';

export const getStaticProps: GetStaticProps = async () => {
  const client = initializeApollo();

  // prefetch
  await client.query({ query: EcusSeoDocument });
  await client.query({ query: EcusHeroDocument });
  await client.query({ query: EcusCardsSectionDocument });
  await client.query({ query: EcusProductImageBlockDocument });
  await client.query({ query: EcusTwoImagesGridDocument });
  await client.query({ query: EcusSliderImageSectionDocument });
  await client.query({ query: EcusPartnersBlogSectionDocument });
  await client.query({ query: NavigationDocument });
  await client.query({ query: FooterDocument });
  await client.query({ query: FormDocument });

  const { data: seoData } = await client.query({
    query: EcusSeoDocument,
  });

  const { data: heroData } = await client.query<
    EcusHeroQuery,
    EcusHeroQueryVariables
  >({
    query: EcusHeroDocument,
  });

  const { data: cardsData } = await client.query<
    EcusCardsSectionQuery,
    EcusCardsSectionQueryVariables
  >({
    query: EcusCardsSectionDocument,
  });

  const { data: productImageBlockData } = await client.query<
    EcusProductImageBlockQuery,
    EcusProductImageBlockQueryVariables
  >({
    query: EcusProductImageBlockDocument,
  });

  const { data: twoImagesGridData } = await client.query<
    EcusTwoImagesGridQuery,
    EcusTwoImagesGridQueryVariables
  >({
    query: EcusTwoImagesGridDocument,
  });

  const { data: sliderImageSectionData } = await client.query<
    EcusSliderImageSectionQuery,
    EcusSliderImageSectionQueryVariables
  >({
    query: EcusSliderImageSectionDocument,
  });

  const { data: partnersBlogSectionData } = await client.query<
    EcusPartnersBlogSectionQuery,
    EcusPartnersBlogSectionQueryVariables
  >({
    query: EcusPartnersBlogSectionDocument,
  });

  const { data: formData } = await client.query<FormQuery, FormQueryVariables>({
    query: FormDocument,
  });

  const { data: footerData } = await client.query<
    FooterQuery,
    FooterQueryVariables
  >({ query: FooterDocument });

  const { data: headerData } = await client.query<
    NavigationQuery,
    NavigationQueryVariables
  >({ query: NavigationDocument });

  return addApolloState(client, {
    props: {
      seoData,
      heroData,
      cardsData,
      productImageBlockData,
      twoImagesGridData,
      sliderImageSectionData,
      partnersBlogSectionData,
      formData,
      footerData,
      headerData,
    },
  });
};

const Ecu: NextPage<InferGetStaticPropsType<typeof getStaticProps>> = ({
  seoData,
  heroData,
  cardsData,
  productImageBlockData,
  twoImagesGridData,
  sliderImageSectionData,
  partnersBlogSectionData,
  formData,
  footerData,
  headerData,
}) => (
  <Layout formData={formData} footerData={footerData} headerData={headerData}>
    <Seo seo={seoData?.ecus?.data?.attributes?.seo} />
    <HeroVideoSection
      hero={heroData?.ecus?.data?.attributes?.heroVideoSection}
    />
    <IconCards
      cards={cardsData?.ecus?.data?.attributes?.cardsSection}
      titleMaxWidth={820}
    />
    {productImageBlockData?.ecus?.data?.attributes?.productImageBlock?.map(
      (block?: ProductImageBlockFragment | null, index?: number) => (
        <EnhancedProductImageBlock
          productImageBlock={block}
          key={`enhancedProductImageBlock-ecu-item-${index}`}
          parentIndex={index || 1}
        />
      ),
    )}
    <TwoImagesGrid
      twoImagesGrid={twoImagesGridData?.ecus?.data?.attributes?.twoImagesGrid}
      titleMaxWidth={460}
      descriptionMaxWidth={420}
    />
    <SliderImage
      sliderImageSection={
        sliderImageSectionData?.ecus?.data?.attributes?.sliderImageSection
      }
      titleMaxWidth={820}
    />
    <Partners
      data={
        partnersBlogSectionData?.ecus?.data?.attributes?.partnersBlogSection
      }
    />
  </Layout>
);

export default Ecu;
